import { useRef, useState, useLayoutEffect, useEffect } from 'react';
import useIsMobile from '../hooks/useIsMobile';

// Single-row tab bar with dynamic overflow. It measures the available width
// and shows as many tabs as fit, pushing the rest under "More ▾". On mobile
// the tabs are tighter and at most `mobileVisible` show; a fixed count used to
// be shown regardless of width, which pushed "More" off the edge of a phone.
//
// tabs: [{ key, label, badge? }]   badge = number shown as a pill
const GAP = 4;

const tabBtn = (active, compact = false) => ({
  padding: compact ? '10px 10px' : '10px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  color: active ? 'var(--accent)' : 'var(--text3)', background: 'transparent', border: 'none',
  borderBottom: active ? '2px solid var(--accent)' : '2px solid transparent',
  fontFamily: 'inherit', transition: 'color 0.15s', whiteSpace: 'nowrap',
  display: 'inline-flex', alignItems: 'center', gap: 6,
});

const Badge = ({ n }) => (
  <span style={{ background: 'var(--danger)', color: '#fff', fontSize: 10, padding: '1px 6px', borderRadius: 10 }}>{n}</span>
);

export default function ProfileTabBar({ tabs, activeKey, onSelect, mobileVisible = 4 }) {
  const isMobile = useIsMobile();
  const rowRef = useRef(null);
  const measureRef = useRef(null);
  const moreRef = useRef(null);
  const [visible, setVisible] = useState(tabs.length);
  const [moreOpen, setMoreOpen] = useState(false);

  const signature = tabs.map(t => `${t.key}:${t.badge || 0}`).join('|');

  useLayoutEffect(() => {
    const cap = isMobile ? mobileVisible : tabs.length;
    const compute = () => {
      const row = rowRef.current, measure = measureRef.current;
      if (!row || !measure) return;
      const avail = row.clientWidth;
      const kids = Array.from(measure.children);
      const widths = kids.map(k => k.offsetWidth + GAP);
      const moreW = widths[tabs.length] || 60;          // last measured child is the More button
      const tabW = widths.slice(0, tabs.length);

      const totalAll = tabW.reduce((s, w) => s + w, 0);
      if (totalAll <= avail && tabs.length <= cap) { setVisible(tabs.length); return; }   // everything fits, no More

      let used = 0, n = 0;
      for (let i = 0; i < tabW.length; i++) {
        used += tabW[i];
        if (used + moreW <= avail) n = i + 1; else break;
      }
      setVisible(Math.max(1, Math.min(n, cap)));
    };

    compute();
    const ro = new ResizeObserver(compute);
    if (rowRef.current) ro.observe(rowRef.current);
    return () => ro.disconnect();
  }, [signature, isMobile, mobileVisible, tabs.length]);

  // Close the More dropdown on outside click
  useEffect(() => {
    if (!moreOpen) return;
    const onDown = (e) => { if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [moreOpen]);

  const shown = tabs.slice(0, visible);
  const overflow = tabs.slice(visible);
  const activeInOverflow = overflow.find(t => t.key === activeKey);
  const pick = (key) => { onSelect(key); setMoreOpen(false); };

  return (
    <div ref={rowRef} style={{ display: 'flex', gap: GAP, borderBottom: '1px solid var(--border)', marginBottom: 20, position: 'relative' }}>
      {shown.map(({ key, label, badge }) => (
        <button key={key} onClick={() => pick(key)} style={tabBtn(activeKey === key, isMobile)}>
          {label}{badge ? <Badge n={badge} /> : null}
        </button>
      ))}

      {overflow.length > 0 && (
        <div ref={moreRef} style={{ position: 'relative' }}>
          <button onClick={() => setMoreOpen(o => !o)} style={tabBtn(!!activeInOverflow, isMobile)}>
            {/* On a phone the label stays "More" (highlighted when the open tab is
                in it): a long tab name here would push past the screen edge. */}
            {activeInOverflow && !isMobile ? activeInOverflow.label : 'More'} ▾
            {overflow.some(t => t.badge) ? <Badge n={overflow.reduce((s, t) => s + (t.badge || 0), 0)} /> : null}
          </button>
          {moreOpen && (
            <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 20, background: '#fff', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', minWidth: 200, padding: 4 }}>
              {overflow.map(({ key, label, badge }) => (
                <button key={key} onClick={() => pick(key)} style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', textAlign: 'left',
                  padding: '9px 12px', fontSize: 13, cursor: 'pointer', borderRadius: 6, border: 'none',
                  background: activeKey === key ? 'var(--bg2)' : 'transparent',
                  color: activeKey === key ? 'var(--accent)' : 'var(--text)', fontWeight: activeKey === key ? 600 : 400,
                }}>
                  {label}{badge ? <Badge n={badge} /> : null}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Hidden measurement row — all tabs + a More button at full width */}
      <div ref={measureRef} aria-hidden="true" style={{ position: 'absolute', visibility: 'hidden', height: 0, overflow: 'hidden', whiteSpace: 'nowrap', display: 'flex', gap: GAP, top: 0, left: 0 }}>
        {tabs.map(({ key, label, badge }) => (
          <button key={key} style={tabBtn(false, isMobile)} tabIndex={-1}>{label}{badge ? <Badge n={badge} /> : null}</button>
        ))}
        <button style={tabBtn(false, isMobile)} tabIndex={-1}>More ▾<Badge n={9} /></button>
      </div>
    </div>
  );
}
